/**
 * Webhook-outbox detail (PR-6a + PR-6b actions).
 *
 * Three tabs: overview (identity + timing), payload (JSON), attempts
 * (one row per HTTP attempt, newest first). PR-6b adds a "Retry" action
 * on dead-lettered rows that re-queues the row into the worker's claim
 * set; reason is captured in the admin audit log.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, RotateCw } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { JsonBlock } from "@/components/payments/JsonBlock";
import { ActionConfirmDialog } from "@/components/observability/ActionConfirmDialog";
import {
  asApiError,
  getWebhookOutboxRow,
  retryWebhookOutbox,
  type WebhookOutboxDetailResponse,
  type WebhookOutboxState,
} from "@/lib/api";

const STATE_VARIANT: Record<WebhookOutboxState, BadgeProps["variant"]> = {
  pending: "muted",
  in_flight: "info",
  delivered: "success",
  dead_lettered: "destructive",
};

export function WebhookOutboxDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ["webhook-outbox", id] as const,
    queryFn: () => getWebhookOutboxRow(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load this outbox row", {
        description: "It may not exist or you may not have access.",
      });
    }
  }, [query.error]);

  if (query.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
          <div>
            <CardTitle className="text-base">Couldn't load this outbox row</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              It may not exist or you may not have access.{" "}
              <Link to="/webhook-outbox" className="text-foreground underline">
                Back to list
              </Link>
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  }
  return <Loaded row={query.data} />;
}

function Loaded({ row }: { row: WebhookOutboxDetailResponse }) {
  const queryClient = useQueryClient();
  const [retryOpen, setRetryOpen] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const retryMutation = useMutation({
    mutationFn: (vars: { reason: string }) =>
      retryWebhookOutbox(row.id, { reason: vars.reason }),
    onSuccess: () => {
      toast.success("Retry queued", {
        description: "The worker will pick it up on the next tick.",
      });
      setRetryOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["webhook-outbox", row.id] });
    },
    onError: (err: unknown) => {
      const body = asApiError(err);
      setRetryError(body?.detail ?? "Couldn't retry this row.");
    },
  });

  return (
    <>
      <div className="mb-6 space-y-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 h-auto px-2 text-muted-foreground"
        >
          <Link to="/webhook-outbox">
            <ArrowLeft />
            Back to webhook outbox
          </Link>
        </Button>

        <Card className="p-5 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-lg font-semibold">
                  {row.event_type}
                </span>
                <Badge variant={STATE_VARIANT[row.state]} className="capitalize">
                  {row.state.replace(/_/g, " ")}
                </Badge>
                <Badge variant="muted" className="capitalize">
                  {row.tenant.environment}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{row.tenant.name}</span>
                <span>·</span>
                <span title={row.endpoint.url}>{row.endpoint.url}</span>
                <span>·</span>
                <span title={row.created_at}>
                  enqueued {new Date(row.created_at).toLocaleString()}
                </span>
              </div>
              <code className="block truncate font-mono text-xs text-muted-foreground/80">
                {row.id}
              </code>
            </div>
            {row.state === "dead_lettered" ? (
              <div className="shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRetryError(null);
                    setRetryOpen(true);
                  }}
                >
                  <RotateCw />
                  Retry
                </Button>
              </div>
            ) : null}
          </div>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="payload">Payload</TabsTrigger>
          <TabsTrigger value="attempts">
            Attempts ({row.delivery_attempts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Outbox ID" mono>
                    {row.id}
                  </Def>
                  <Def label="Event type">{row.event_type}</Def>
                  <Def label="Dedup key" mono>
                    {row.dedup_key}
                  </Def>
                  <Def label="Endpoint" mono>
                    {row.endpoint.url}
                  </Def>
                  <Def label="Endpoint status" mono>
                    {row.endpoint.status}
                  </Def>
                </Defs>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Timing + state</CardTitle>
              </CardHeader>
              <CardContent>
                <Defs>
                  <Def label="Created">
                    {new Date(row.created_at).toLocaleString()}
                  </Def>
                  <Def label="Available at">
                    {new Date(row.available_at).toLocaleString()}
                  </Def>
                  <Def label="Delivered at">
                    {row.delivered_at
                      ? new Date(row.delivered_at).toLocaleString()
                      : "—"}
                  </Def>
                  <Def label="Dead-lettered at">
                    {row.dead_lettered_at
                      ? new Date(row.dead_lettered_at).toLocaleString()
                      : "—"}
                  </Def>
                </Defs>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="payload">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Outbound payload</CardTitle>
              <p className="text-xs text-muted-foreground">
                The body the producer wrote at enqueue time — same shape the
                operator's webhook consumer receives over HTTPS.
              </p>
            </CardHeader>
            <CardContent>
              <JsonBlock value={row.payload} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attempts">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Delivery attempts</CardTitle>
              <p className="text-xs text-muted-foreground">
                One row per HTTP attempt, newest first. Response bodies are
                truncated server-side to bound storage.
              </p>
            </CardHeader>
            <CardContent>
              {row.delivery_attempts.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No delivery attempts recorded yet.
                </p>
              ) : (
                <ul className="space-y-3">
                  {row.delivery_attempts.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-md border bg-muted/20 px-3 py-2 text-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          {a.response_status !== null ? (
                            <Badge
                              variant={
                                a.response_status >= 200 && a.response_status < 300
                                  ? "success"
                                  : "destructive"
                              }
                              className="font-mono"
                            >
                              {a.response_status}
                            </Badge>
                          ) : (
                            <Badge variant="destructive">network error</Badge>
                          )}
                          {a.duration_ms !== null ? (
                            <span className="text-xs text-muted-foreground">
                              {a.duration_ms}ms
                            </span>
                          ) : null}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(a.attempted_at).toLocaleString()}
                        </span>
                      </div>
                      {a.error ? (
                        <code className="mt-2 block break-all font-mono text-xs text-destructive">
                          {a.error}
                        </code>
                      ) : null}
                      {a.response_body_truncated ? (
                        <pre className="mt-2 max-h-32 overflow-auto rounded bg-background p-2 font-mono text-xs">
                          {a.response_body_truncated}
                        </pre>
                      ) : null}
                      {a.request_signature ? (
                        <div className="mt-2 break-all font-mono text-[10px] text-muted-foreground">
                          <span className="mr-1">X-Senti-Signature:</span>
                          {a.request_signature}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ActionConfirmDialog
        open={retryOpen}
        onOpenChange={(open) => {
          setRetryOpen(open);
          if (!open) setRetryError(null);
        }}
        title="Retry this webhook delivery"
        description={
          "Clears dead_lettered_at, resets the attempt counter, and marks the row due now. " +
          "The worker will pick it up on the next tick. A reason is required and is written to the admin audit log."
        }
        confirmLabel="Retry"
        pending={retryMutation.isPending}
        error={retryError}
        onSubmit={({ reason }) => retryMutation.mutate({ reason })}
      />
    </>
  );
}

function Defs({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-3">{children}</dl>;
}

function Def({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd
        className={
          mono
            ? "min-w-0 break-all font-mono text-xs sm:text-right"
            : "min-w-0 text-sm sm:text-right"
        }
      >
        {children}
      </dd>
    </div>
  );
}

export default WebhookOutboxDetailPage;
